import {
  OUTBOUND_CALLING_OPS as types
} from '@store/constants/outboundCallingConstants';
import { AGENT_STATUS } from '@views/OutboundCallingDashboard/constant';

const initialState = {
  // Config State
  config: {
    data: null,
    isLoading: false,
    error: null
  },

  // Agent Dashboard State
  agentDashboard: {
    agent: {
      status: AGENT_STATUS.INACTIVE,
      lastUpdatedAt: null,
      isLoading: false,
      error: null
    },
    tickets: {
      data: [],
      isLoading: false,
      isError: false,
      error: null,
      totalPages: 0,
      totalResults: 0,
      currentPage: 0
    }
  },

  // Admin Dashboard State
  adminDashboard: {
    // Overall stats
    stats: {
      data: {
        agentAvailability: {
          available: 0,
          offline: 0,
          onCall: 0
        },
        queueDetails: {
          length: 0
        },
        overallCallAnalytics: {
          total: 0,
          completed: 0,
          failed: 0
        }
      },
      isLoading: false,
      error: null
    },
    // Available Agents
    availableAgents: {
      data: null,
      isLoading: false,
      error: null
    },
    // Tickets Queue (Open Tickets)
    ticketsQueue: {
      data: null,
      isLoading: false,
      error: null
    },
    // Closed Tickets
    closedTickets: {
      data: null,
      isLoading: false,
      error: null
    }
  },

  currentTicket: {
    ticket: null,
    callLogs: {
      data: [],
      isLoading: false,
      error: null
    },
    notesToAdd: '',
    isUpdating: false,
    error: null
  }
};

const outboundCallingReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    // Config Operations
    case types.GETTING_OUTBOUND_CONFIG:
      return {
        ...state,
        config: {
          ...state.config,
          isLoading: true,
          error: null,
          data: null
        }
      };

    case types.GET_OUTBOUND_CONFIG_SUCCESS:
      return {
        ...state,
        config: {
          ...state.config,
          isLoading: false,
          error: null,
          data: payload
        }
      };

    case types.GET_OUTBOUND_CONFIG_FAILURE:
      return {
        ...state,
        config: {
          ...state.config,
          isLoading: false,
          error: payload,
          data: null
        }
      };

    // Agent Status Operations
    case types.GETTING_AGENT_STATUS:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          agent: {
            ...state.agentDashboard.agent,
            isLoading: true,
            error: null
          }
        }
      };

    case types.GET_AGENT_STATUS_SUCCESS:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          agent: {
            ...state.agentDashboard.agent,
            isLoading: false,
            // Handle both payload formats: { status, lastUpdated } OR { isOnline }
            status: payload.status || (payload.isOnline ? AGENT_STATUS.ACTIVE : AGENT_STATUS.INACTIVE),
            lastUpdatedAt: payload?.lastActivityAt || new Date().toISOString()
          }
        }
      };

    case types.GET_AGENT_STATUS_FAILURE:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          agent: {
            ...state.agentDashboard.agent,
            isLoading: false,
            error: payload
          }
        }
      };

    case types.SETTING_AGENT_STATUS:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          agent: {
            ...state.agentDashboard.agent,
            isLoading: true,
            error: null
          }
        }
      };

    case types.SET_AGENT_STATUS_SUCCESS:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          agent: {
            ...state.agentDashboard.agent,
            isLoading: false,
            // Handle both payload formats: { status, lastUpdated } OR { isOnline }
            // status: payload.status || (payload.isOnline ? AGENT_STATUS.ACTIVE : AGENT_STATUS.INACTIVE),
            // lastUpdatedAt: payload.lastUpdated || new Date().toISOString()
          }
        }
      };

    case types.SET_AGENT_STATUS_FAILURE:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          agent: {
            ...state.agentDashboard.agent,
            isLoading: false,
            error: payload
          }
        }
      };

    // Ticket Operations
    case types.GETTING_TICKETS:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          tickets: {
            ...state.agentDashboard.tickets,
            isLoading: true,
            isError: false,
            error: null
          }
        }
      };

    case types.GET_TICKETS_SUCCESS:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          tickets: {
            ...state.agentDashboard.tickets,
            isLoading: false,
            data: payload.data,
            totalResults: payload.totalResults,
            totalPages: payload.totalPages,
            currentPage: payload.currentPage
          }
        }
      };

    case types.GET_TICKETS_FAILURE:
      return {
        ...state,
        agentDashboard: {
          ...state.agentDashboard,
          tickets: {
            ...state.agentDashboard.tickets,
            isLoading: false,
            isError: true,
            error: payload
          }
        }
      };

    // Current Ticket Operations
    case types.SET_CURRENT_TICKET:
      return {
        ...state,
        currentTicket: {
          ...state.currentTicket,
          ticket: payload
        }
      };

    case types.CLEAR_CURRENT_TICKET:
      return {
        ...state,
        currentTicket: {
          ticket: null,
          callLogs: {
            data: [],
            isLoading: false,
            error: null
          },
          notesToAdd: '',
          isUpdating: false,
          error: null
        }
      };

    case types.UPDATING_TICKET:
      return {
        ...state,
        currentTicket: {
          ...state.currentTicket,
          isUpdating: true,
          error: null
        }
      };

    case types.UPDATE_TICKET_SUCCESS:
      return {
        ...state,
        currentTicket: {
          ...state.currentTicket,
          isUpdating: false,
          ticket: payload
        },
        agentDashboard: {
          ...state.agentDashboard,
          tickets: {
            ...state.agentDashboard.tickets,
            data: state.agentDashboard.tickets.data.map((ticket) =>
              ticket.id === payload.id ? payload : ticket
            )
          }
        }
      };

    case types.UPDATE_TICKET_FAILURE:
      return {
        ...state,
        currentTicket: {
          ...state.currentTicket,
          isUpdating: false,
          error: payload
        }
      };

    // Admin Dashboard Operations
    case types.GETTING_ADMIN_DASHBOARD_STATS:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          stats: {
            ...state.adminDashboard.stats,
            isLoading: true,
            error: null
          }
        }
      };

    case types.GET_ADMIN_DASHBOARD_STATS_SUCCESS:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          stats: {
            ...state.adminDashboard.stats,
            isLoading: false,
            data: payload,
            error: null
          }
        }
      };

    case types.GET_ADMIN_DASHBOARD_STATS_FAILURE:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          stats: {
            ...state.adminDashboard.stats,
            isLoading: false,
            error: payload
          }
        }
      };

    // Admin Dashboard - Available Agents Operations
    case types.GETTING_AVAILABLE_AGENTS:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          availableAgents: {
            ...state.adminDashboard.availableAgents,
            isLoading: true,
            error: null
          }
        }
      };

    case types.GET_AVAILABLE_AGENTS_SUCCESS:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          availableAgents: {
            ...state.adminDashboard.availableAgents,
            isLoading: false,
            data: payload,
            error: null
          }
        }
      };

    case types.GET_AVAILABLE_AGENTS_FAILURE:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          availableAgents: {
            ...state.adminDashboard.availableAgents,
            isLoading: false,
            error: payload
          }
        }
      };

    // Admin Dashboard - Tickets Queue Operations (Open Tickets)
    case types.GETTING_TICKETS_QUEUE:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          ticketsQueue: {
            ...state.adminDashboard.ticketsQueue,
            isLoading: true,
            error: null
          }
        }
      };

    case types.GET_TICKETS_QUEUE_SUCCESS:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          ticketsQueue: {
            ...state.adminDashboard.ticketsQueue,
            isLoading: false,
            data: payload,
            error: null
          }
        }
      };

    case types.GET_TICKETS_QUEUE_FAILURE:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          ticketsQueue: {
            ...state.adminDashboard.ticketsQueue,
            isLoading: false,
            error: payload
          }
        }
      };

    // Admin Dashboard - Closed Tickets Operations
    case types.GETTING_CLOSED_TICKETS:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          closedTickets: {
            ...state.adminDashboard.closedTickets,
            isLoading: true,
            error: null
          }
        }
      };

    case types.GET_CLOSED_TICKETS_SUCCESS:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          closedTickets: {
            ...state.adminDashboard.closedTickets,
            isLoading: false,
            data: payload,
            error: null
          }
        }
      };

    case types.GET_CLOSED_TICKETS_FAILURE:
      return {
        ...state,
        adminDashboard: {
          ...state.adminDashboard,
          closedTickets: {
            ...state.adminDashboard.closedTickets,
            isLoading: false,
            error: payload
          }
        }
      };

    default:
      return state;
  }
};

export default outboundCallingReducer;
